import React, { useMemo } from "react";
import {
  Typography,
  Stack,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ExpandMore,
  FolderOpen,
  Description,
  Build,
  Download,
} from "@mui/icons-material";
import { DownloadFile } from "./AttachmentActions";
import { fetcher } from "../../utils/axios.js";

export default function AttachmentOverview({ payload }) {
  const { project } = payload;

  // Memoized function to get attachments from different sources
  const attachmentsBySource = useMemo(() => {
    const attachmentsBySource = {
      opstart: {
        title: "Opstart - Project bijlagen",
        icon: <FolderOpen color="primary" />,
        attachments: (project.attachments || []).map((attachment) => ({
          ...attachment,
          source: {
            phase: "",
            ontwerploop: "",
            step: "",
          },
        })),
        description: "Bijlagen die zijn toegevoegd tijdens de opstartfase van het project.",
      },
      adviseren: [],
      toetsen: [],
      vergelijken: [],
      beoordelen: [],
    };

    // Collect attachments from all phases and loops
    if (project.phases) {
      project.phases.forEach((phase, phaseIndex) => {
        if (phase.loops) {
          phase.loops.forEach((loop, loopIndex) => {
            // Adviseren attachments (from advice_by_themes)
            if (loop.advice_by_themes) {
              Object.values(loop.advice_by_themes).forEach((advice, adviceIndex) => {
                if (advice && advice.attachments) {
                  // Add source information to each attachment
                  const attachmentsWithSource = advice.attachments.map((attachment) => ({
                    ...attachment,
                    source: {
                      phase: phase.phase_name,
                      ontwerploop: loop.name || `Ontwerpronde ${loopIndex + 1}`,
                      step: "",
                    },
                  }));
                  attachmentsBySource.adviseren.push(...attachmentsWithSource);
                }
              });
            } else {
              console.log("No advice_by_themes found");
            }

            // Toetsen attachments (from designs)
            if (loop.designs) {
              [loop.reference_design, ...loop.designs].forEach((design, designIndex) => {
                if (design.attachments) {
                  // Add source information to each attachment
                  const attachmentsWithSource = design.attachments.map((attachment) => ({
                    ...attachment,
                    source: {
                      phase: phase.phase_name,
                      ontwerploop: loop.name || `Ontwerpronde ${loopIndex + 1}`,
                      step: design.is_reference ? "Referentie" : "Toetsen",
                    },
                  }));
                  attachmentsBySource.toetsen.push(...attachmentsWithSource);
                }
              });
            } else {
              console.log("No designs found");
            }
          });
        }
      });
    }

    return attachmentsBySource;
  }, [project]);

  // Download handler for attachments
  const handleDownload = async (attachment) => {
    try {
      const response = await fetcher(`/attachments/${attachment.id}/download/`, "blob");
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
        name: attachment.filename,
      });
      const blobURL = URL.createObjectURL(blob);
      DownloadFile(blobURL, attachment.filename);
    } catch (error) {
      console.error("Error downloading attachment:", error);
    }
  };

  const AttachmentCard = ({ attachment }) => {
    return (
      <Box
        sx={{ mb: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              {attachment.name}
            </Typography>
            {attachment.source.phase && (
              <>
                <Typography variant="caption">{attachment.source.phase} - {attachment.source.ontwerploop}{attachment.source.step && ` - ${attachment.source.step}`}</Typography>
                <br />
              </>
              )
            }
            <Typography variant="caption">{attachment.filename}</Typography>

            {attachment.description && (
              <>
              <br/>
              <Typography variant="caption" sx={{ mb: 1 }}>
                {attachment.description}
              </Typography>
              </>
            )}
          </Box>
          <Tooltip title="Download bestand">
            <IconButton onClick={() => handleDownload(attachment)} color="primary" size="small">
              <Download />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    );
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h6" color="text.secondary">
        Hier vindt u alle bijlagen die zijn toegevoegd tijdens de verschillende fases van dit project.
      </Typography>

      {/* Opstart - Project bijlagen */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            {attachmentsBySource.opstart.icon}
            <Typography variant="subtitle2">{attachmentsBySource.opstart.title}</Typography>
            <Typography variant="caption">({attachmentsBySource.opstart.attachments.length} bestanden)</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {attachmentsBySource.opstart.description}
            </Typography>
            {attachmentsBySource.opstart.attachments.length > 0 ? (
              <Box>
                {attachmentsBySource.opstart.attachments.map((attachment, index) => (
                  <AttachmentCard key={index} attachment={attachment} />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                Geen bijlagen toegevoegd in de opstartfase.
              </Typography>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Adviseren bijlagen */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Description color="primary" />
            <Typography variant="subtitle2">Adviseren - Advies bijlagen</Typography>
            <Typography variant="caption">({attachmentsBySource.adviseren.length} bestanden)</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Bijlagen die zijn toegevoegd tijdens het adviseren van duurzaamheidsthema's.
            </Typography>
            {attachmentsBySource.adviseren.length > 0 ? (
              <Box>
                {attachmentsBySource.adviseren.map((attachment, index) => (
                  <AttachmentCard key={index} attachment={attachment} />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                Geen bijlagen toegevoegd tijdens het adviseren.
              </Typography>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Toetsen bijlagen */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Build color="primary" />
            <Typography variant="subtitle2">Toetsen - Ontwerp bijlagen</Typography>
            <Typography variant="caption">({attachmentsBySource.toetsen.length} bestanden)</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Bijlagen die zijn toegevoegd bij de verschillende ontwerpen tijdens het toetsen.
            </Typography>
            {attachmentsBySource.toetsen.length > 0 ? (
              <Box>
                {attachmentsBySource.toetsen.map((attachment, index) => (
                  <AttachmentCard key={index} attachment={attachment} />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                Geen bijlagen toegevoegd tijdens het toetsen.
              </Typography>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Vergelijken bijlagen */}
      {/* <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Assessment color="primary" />
                        <Typography variant="h6">
                            Vergelijken - Vergelijkingsdocumenten
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            (0 bestanden)
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            Documenten die zijn toegevoegd tijdens het vergelijken van ontwerpen.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Geen bijlagen toegevoegd tijdens het vergelijken.
                        </Typography>
                    </Stack>
                </AccordionDetails>
            </Accordion> */}

      {/* Beoordelen bijlagen */}
      {/* <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Assessment color="primary" />
                        <Typography variant="h6">
                            Beoordelen - Evaluatiedocumenten
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            (0 bestanden)
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            Documenten die zijn toegevoegd tijdens de beoordeling van ontwerpen.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Geen bijlagen toegevoegd tijdens het beoordelen.
                        </Typography>
                    </Stack>
                </AccordionDetails>
            </Accordion> */}
    </Stack>
  );
}
